/**
 * V4-ONBOARDING-TOUR 2026-06-18
 *
 * Tour produit (bulles explicatives) au premier login, basé sur react-joyride.
 *
 * Objectif : expliquer la UI à un utilisateur qui découvre Raiso — pages, boutons et
 * fonctionnalités clés — avec des parcours distincts USER vs AGENCY (et desktop vs mobile).
 *
 * Choix d'implémentation :
 *  - **Styling isolé** : tout le style passe par la prop `styles` de Joyride (couleurs
 *    lues depuis les CSS vars du thème). On ne touche PAS au CSS global → pas de conflit
 *    avec le Tailwind figé (cf. V4-FRONT-TAILWIND-FROZEN-CSS).
 *  - **Cibles robustes** : on ne garde que les steps dont la cible existe réellement
 *    dans le DOM au moment du lancement (items sidebar lockés, absence sur mobile, etc.).
 *    Évite que Joyride bloque sur une cible introuvable (risque documenté du plan).
 *  - **Mobile** : la sidebar desktop n'existe pas → le tour mobile se limite au chrome
 *    visible (space switcher pour l'agence + bouton Générer). Les sections de nav vivent
 *    dans le burger (fermé) et ne sont pas ciblées.
 *  - **GUEST** : pas de tour (lecture seule) — le caller ne monte pas ce composant pour eux.
 */
import { useEffect, useMemo, useState } from 'react';
import Joyride, { type CallBackProps, type Step, ACTIONS, STATUS } from 'react-joyride';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SIDEBAR_ITEMS } from '../../../utils/Constants';

interface OnboardingTourProps {
  /** True quand le tour doit tourner (cf. useProductTour). */
  run: boolean;
  /** Appelé quand l'utilisateur termine ou passe le tour. */
  onFinish: () => void;
  /** Type de compte : adapte le parcours (AGENCY ajoute le space switcher). */
  accountType?: string;
  /** Vue mobile : limite les steps au chrome visible. */
  isMobile: boolean;
}

/** Lit une CSS var du thème, avec fallback. */
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

export function OnboardingTour({ run, onFinish, accountType, isMobile }: OnboardingTourProps) {
  const { t } = useLanguage();
  const isAgency = accountType === 'AGENCY';

  // Construit la liste candidate de steps selon le contexte (desktop/mobile, user/agency).
  const candidateSteps = useMemo<Step[]>(() => {
    const tour = t.tour;
    const steps: Step[] = [];

    // Agence : commencer par le sélecteur d'espace (multi-clients) — présent desktop ET mobile.
    if (isAgency) {
      steps.push({
        target: '[data-tour="space-switcher"]',
        title: tour?.spaceSwitcherTitle ?? 'Vos espaces clients',
        content: tour?.spaceSwitcherDesc ?? 'Chaque espace correspond à un de vos clients. Changez d\'espace ici pour configurer et publier pour le bon client.',
        disableBeacon: true,
        placement: 'bottom',
      });
      // Page de gestion des espaces (item sidebar desktop). Le filtre DOM ci-dessous
      // retire ce step sur mobile (item dans le burger fermé) ou pour les non-agences.
      steps.push({
        target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.SPACES}"]`,
        title: tour?.spacesManageTitle ?? 'Gérer vos espaces',
        content: tour?.spacesManageDesc ?? 'Créez, renommez ou archivez vos espaces ici, et invitez vos clients en lecture seule.',
        placement: 'right',
      });
    }

    if (!isMobile) {
      // Desktop : les items de la sidebar gauche (cibles = data-testid existants).
      steps.push(
        {
          target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.POSITIONING}"]`,
          title: tour?.positioningTitle ?? 'Positionnement',
          content: tour?.positioningDesc ?? 'Définissez votre identité, votre ton et votre cible. C\'est la base de tout ce que Raiso génère.',
          disableBeacon: !isAgency,
          placement: 'right',
        },
        {
          target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.WATCH}"]`,
          title: tour?.watchTitle ?? 'Centres d\'intérêt',
          content: tour?.watchDesc ?? 'Indiquez les thématiques sur lesquelles vous souhaitez communiquer. Raiso surveille l\'actualité associée.',
          placement: 'right',
        },
        {
          target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.SOURCES}"]`,
          title: tour?.sourcesTitle ?? 'Sources',
          content: tour?.sourcesDesc ?? 'Ajoutez des flux RSS, sites ou chaînes YouTube : Raiso s\'en inspire pour générer des posts pertinents.',
          placement: 'right',
        },
        {
          target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.CHANNELS}"]`,
          title: tour?.channelsTitle ?? 'Canaux',
          content: tour?.channelsDesc ?? 'Connectez vos réseaux (LinkedIn, X, Instagram…) pour publier directement depuis Raiso.',
          placement: 'right',
        },
        {
          target: `[data-testid="sidebar-item-${SIDEBAR_ITEMS.ENGAGEMENT}"]`,
          title: tour?.engagementTitle ?? 'Engagement',
          content: tour?.engagementDesc ?? 'Retrouvez ici les commentaires reçus sur vos publications pour y répondre.',
          placement: 'right',
        },
      );
    }

    // Bouton Générer — présent desktop (header) et mobile (toolbar).
    steps.push({
      target: '[data-tour="generate"]',
      title: tour?.generateTitle ?? 'Générer des posts',
      content: tour?.generateDesc ?? 'Une fois la configuration prête, lancez la génération de posts ici. Vous pourrez les relire, éditer et publier.',
      placement: 'bottom',
      disableBeacon: true,
    });

    return steps;
  }, [t.tour, isAgency, isMobile]);

  // Ne garder que les steps dont la cible existe réellement dans le DOM au lancement.
  // Évite que Joyride bloque sur une cible introuvable (item lock, absence mobile).
  const [steps, setSteps] = useState<Step[]>([]);
  useEffect(() => {
    if (!run) {
      setSteps([]);
      return;
    }
    const present = candidateSteps.filter((s) => {
      const sel = typeof s.target === 'string' ? s.target : null;
      if (!sel) return true;
      try {
        return !!document.querySelector(sel);
      } catch {
        return false;
      }
    });
    setSteps(present);
  }, [run, candidateSteps]);

  // V4-ONBOARDING-TOUR-CLOSE-STUCK-OVERLAY (2026-07-03) — le bouton "✕" (Fermer) sur
  // une bulle Joyride émet action=ACTIONS.CLOSE avec un status qui reste RUNNING
  // (ce n'est ni FINISHED ni SKIPPED). Sans ce cas, onFinish() n'était jamais appelé :
  // le tour restait "actif" (run=true) mais sans tooltip visible, et son overlay plein
  // écran continuait à intercepter tous les clics — l'utilisateur se retrouvait bloqué
  // sur le dashboard au premier login, incapable de cliquer quoi que ce soit.
  const handleCallback = (data: CallBackProps) => {
    const finished: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finished.includes(data.status) || data.action === ACTIONS.CLOSE) {
      onFinish();
    }
  };

  // Pas de cible disponible (ex: dashboard pas encore monté) → ne rien rendre.
  if (!run || steps.length === 0) return null;

  const primary = cssVar('--color-primary', '#5500FF');
  const textPrimary = cssVar('--color-text-primary', '#111827');
  const bg = cssVar('--color-background', '#ffffff');

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling
      scrollToFirstStep={false}
      callback={handleCallback}
      locale={{
        back: t.tour?.back ?? 'Précédent',
        close: t.tour?.close ?? 'Fermer',
        last: t.tour?.last ?? 'Terminer',
        next: t.tour?.next ?? 'Suivant',
        skip: t.tour?.skip ?? 'Passer',
      }}
      styles={{
        options: {
          primaryColor: primary,
          textColor: textPrimary,
          backgroundColor: bg,
          arrowColor: bg,
          zIndex: 100000,
          width: isMobile ? 300 : 360,
        },
        tooltipTitle: { fontWeight: 700, fontSize: isMobile ? 15 : 16 },
        tooltipContent: { fontSize: isMobile ? 13 : 14, lineHeight: 1.5 },
        buttonNext: { borderRadius: 8, fontWeight: 600 },
        buttonSkip: { color: textPrimary, opacity: 0.6 },
      }}
    />
  );
}
